import React from 'react';
import { Trophy, Gift, Lock, Star, TrendingUp } from 'lucide-react';

export const LoyaltyTiersPage = () => {
  const currentSpent = 1278;
  const nextTierTarget = 7000;
  const progress = (currentSpent / nextTierTarget) * 100;
  const remainingAmount = nextTierTarget - currentSpent;

  const tiers = [
    {
      name: 'Bronze',
      icon: Trophy,
      reward: '10% Aaladipattiyan Coins on every purchase',
      range: '₹0 - ₹7,000',
      benefits: [
        'Earn 10% coins on every purchase',
        'Birthday special offers',
        'Early access to new menu items',
        'Free delivery on orders above ₹500'
      ],
      isUnlocked: true,
      isActive: true,
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      name: 'Silver',
      icon: Gift,
      reward: '15% Aaladipattiyan Coins on every purchase',
      range: '₹7,001 - ₹12,000',
      benefits: [
        'Earn 15% coins on every purchase',
        'Priority customer support',
        'Exclusive silver member discounts',
        'Free appetizer on special occasions',
        'Extended coin validity'
      ],
      isUnlocked: false,
      isActive: false,
      color: 'from-gray-400 to-gray-600'
    },
    {
      name: 'Gold',
      icon: Star,
      reward: '20% Aaladipattiyan Coins on every purchase',
      range: '₹12,001 and upward',
      benefits: [
        'Earn 20% coins on every purchase',
        'VIP table reservations',
        'Complimentary dessert with every meal',
        'Personal chef recommendations',
        'Exclusive gold member events',
        'Lifetime coin validity'
      ],
      isUnlocked: false,
      isActive: false,
      color: 'from-yellow-500 to-yellow-700'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Loyalty Tiers</h1>
        <p className="text-gray-600">Unlock exclusive benefits as you spend more</p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Your Progress</h2>
            <p className="text-gray-600">Keep spending to unlock the next tier</p>
          </div>
          <div className="flex items-center gap-2 bg-green-100 px-4 py-2 rounded-full">
            <TrendingUp size={20} className="text-green-600" />
            <span className="font-semibold text-green-800">Bronze Member</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">₹0</span>
            <span className="text-sm font-medium text-gray-600">₹{nextTierTarget} total spent</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-3">
            <div
              className="bg-gradient-to-r from-green-500 to-green-600 h-4 rounded-full transition-all duration-500 relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-0 h-4 w-4 bg-white rounded-full border-2 border-green-500 transform translate-x-1/2"></div>
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">You've spent ₹{currentSpent} till now</span>
            <span className="font-semibold text-gray-800">₹{remainingAmount} away from Silver</span>
          </div>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {tiers.map((tier) => (
          <div
            key={tier.name}
            className={`rounded-2xl shadow-lg border transition-all duration-200 hover:shadow-xl ${
              tier.isActive
                ? 'bg-white border-green-200 ring-2 ring-green-500 ring-opacity-50'
                : tier.isUnlocked
                ? 'bg-white border-gray-200'
                : 'bg-gray-50 border-gray-300'
            }`}
          >
            <div className={`p-6 rounded-t-2xl bg-gradient-to-r ${tier.color}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white bg-opacity-20 rounded-full">
                    <tier.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{tier.name}</h3>
                </div>
                {tier.isActive && (
                  <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-semibold">Current</span>
                  </div>
                )}
                {!tier.isUnlocked && (
                  <Lock size={20} className="text-white opacity-70" />
                )}
              </div>
              <p className="text-white text-opacity-90 font-medium">{tier.reward}</p>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <span className="text-sm font-semibold text-gray-600">Spending Range</span>
                <p className="text-lg font-bold text-gray-800">{tier.range}</p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-800">Benefits:</h4>
                <ul className="space-y-2">
                  {tier.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {tier.isActive && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    🎉 You're currently enjoying all {tier.name} tier benefits!
                  </p>
                </div>
              )}

              {!tier.isUnlocked && (
                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Spend ₹{tier.name === 'Silver' ? remainingAmount : '12,001'} more to unlock this tier
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};